import React, {useContext} from "react"
import AppContext from "../../appContext"
import SampleDepth from "../../controllers/SampleDepth"
import NullableMeasureValue from "../../controllers/NullableMeasureValue"
import {customUnits, unitCustom} from "../program/CustomUnits"
import {NullableMeasureValueField} from "../program/MeasureValueField"
import NumberNullable from "../../components/NumberNullable"
import Number from "../../components/Number"
import {onInputHandler} from "../../wrapper"
import {KeyOfType} from "../../components/Fields"
import {RowWithId} from "./limeSoilUtils"
import MeasurementUnit from "../../controllers/MeasurementUnit"

interface LimeSoilRowEditorProps {
    editRow: RowWithId
    setEditRow: (row: RowWithId) => void
    system: MeasurementUnit
    meq100gMode: boolean
}

/**
 * Shared editor for a single lime soil sample row. Fields follow the canonical ITEST Soil Export /
 * Online Liming template order and header text so the manual form mirrors the CSV import/export.
 * Used by every soil dialog (data upload, playfield, summary) so the field set never drifts apart.
 */
const LimeSoilRowEditor: React.FC<LimeSoilRowEditorProps> = ({editRow, setEditRow, system, meq100gMode}) => {
    const app = useContext(AppContext)
    // K/Ca/Mg/Na can be meq/100g in some regions; micro-elements are always mg/kg (metric) or ppm (imperial).
    const unitLabel = meq100gMode ? 'meq/100g' : unitCustom(system, customUnits.mgKg_ppm)
    const microUnit = unitCustom(system, customUnits.mgKg_ppm)

    function nullableMeasure(label: string, key: KeyOfType<RowWithId, NullableMeasureValue>) {
        return <td>
            <div>{label}</div>
            <NullableMeasureValueField
                unit={customUnits.mgKg_ppm}
                value={editRow[key]}
                update={v => setEditRow({...editRow, [key]: v})}
            />
        </td>
    }

    function nullableNumber(label: string, key: KeyOfType<RowWithId, number | null>) {
        return <td>
            <div>{label}</div>
            <NumberNullable value={editRow[key]} change={v => setEditRow({...editRow, [key]: v})}/>
        </td>
    }

    return <table className="w-full">
        <tbody>
            <tr>
                <td>
                    <div>{app.word('block')}</div>
                    <div className="input bg-gray-100">{editRow.landName || '—'}</div>
                </td>
                <td>
                    <div>Sample Number</div>
                    <input className="input" type="text" value={editRow.referenceNr}
                           onInput={onInputHandler(v => setEditRow({...editRow, referenceNr: v}))}/>
                </td>
                <td>
                    <div>Lab number</div>
                    <input className="input" type="text" value={editRow.labNumber ?? ''}
                           onInput={onInputHandler(v => setEditRow({...editRow, labNumber: v || null}))}/>
                </td>
                {nullableNumber('Latitude', 'latitude')}
                {nullableNumber('Longitude', 'longitude')}
                <td>
                    <div>Sample depth</div>
                    <select
                        className="input"
                        value={editRow.sampleDepth}
                        onChange={e => setEditRow({...editRow, sampleDepth: e.target.value as SampleDepth})}
                    >
                        <option value={SampleDepth.TopSoil}>{app.word(SampleDepth.TopSoil)}</option>
                        <option value={SampleDepth.SubSoil}>{app.word(SampleDepth.SubSoil)}</option>
                        <option value={SampleDepth.TopSoilMicros}>{app.word(SampleDepth.TopSoilMicros)}</option>
                    </select>
                </td>
            </tr>

            <tr>
                {nullableNumber('(Ca+Mg)/K', 'caMgKRatio')}
                {nullableNumber('Ca%', 'caPercent')}
                {nullableNumber('Ca:Mg', 'caMgRatio')}
                <td>
                    <div>Density (g/cm3)</div>
                    <Number value={editRow.density} change={v => setEditRow({...editRow, density: v})}/>
                </td>
                {nullableNumber('K%', 'kPercent')}
                {nullableNumber('Mg%', 'mgPercent')}
            </tr>

            <tr>
                {nullableNumber('Mg:K', 'mgKRatio')}
                {nullableNumber('Na%', 'naPercent')}
                {nullableNumber('Na:K', 'naKRatio')}
                {nullableMeasure('P Bray I', 'pBrayI')}
                {nullableMeasure('P Bray II', 'pBrayII')}
                {nullableMeasure('P Olsen', 'pOlsen')}
            </tr>

            <tr>
                {nullableMeasure('P Mehlich III', 'pMehlichIII')}
                {nullableMeasure('Sulphur', 'sulfur')}
                {nullableNumber('pH (H2O)', 'phH2O')}
                {nullableNumber('pH (KCl)', 'phKcl')}
                {nullableMeasure(`K (${unitLabel})`, 'k')}
                {nullableMeasure(`Ca (${unitLabel})`, 'ca')}
            </tr>

            <tr>
                {nullableMeasure(`Mg (${unitLabel})`, 'mg')}
                {nullableMeasure(`Na (${unitLabel})`, 'na')}
                {nullableMeasure(`Fe (${microUnit})`, 'fe')}
                {nullableMeasure(`Mn (${microUnit})`, 'mn')}
                {nullableMeasure(`Zn (${microUnit})`, 'zn')}
                {nullableMeasure(`Cu (${microUnit})`, 'cu')}
            </tr>

            <tr>
                {nullableNumber('Exch Acid KCL', 'exchH')}
                {nullableNumber('Exch Acid K2SO4', 'exchAcidK2SO4')}
                {nullableNumber('Exch Acid M3', 'exchAcidM3')}
                {nullableNumber('CEC / T-value', 'cec')}
                {nullableNumber('Acid saturation / H%', 'acidSat')}
                {nullableNumber('S-value', 'sValue')}
            </tr>

            <tr>
                {nullableNumber('Buffer pH', 'bufferPh')}
                {nullableNumber('Organic matter %', 'organicMatter')}
                {nullableNumber('CaCO3 (%)', 'caCO3')}
            </tr>
        </tbody>
    </table>
}

export default LimeSoilRowEditor
